import React, { useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { ShoppingCartOutlined, AccountCircle } from "@mui/icons-material";
import Badge from "@mui/material/Badge";
import { mobile, tabletLandscape, tabletPortrait } from "../responsive";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { emptyReducer } from "../redux/userRedux";
import { Button, Menu, MenuItem, Fade } from "@mui/material";

/* ------------------ Shared Responsive Menu Style ------------------ */
const slideMenu = css`
  flex-direction: column;
  align-items: flex-start;
  position: fixed;
  right: ${({ isOpened }) => (isOpened ? "0" : "-230px")};
  width: 200px;
  height: 100vh;
  background-color: #afe6ff;
  box-shadow: 0 40px 30px rgba(0, 0, 0, 0.2);
  padding: 20px 0 0 10px;
  z-index: 3;
  transition: 0.35s ease-in-out;
`;

/* ------------------ Styled Components ------------------ */

const Container = styled.div`
  position: sticky;
  top: ${({ hide }) => (hide ? "-90px" : "0")};
  transition: top 0.3s ease;
  z-index: 1000;
  background-color: #c9eeff;
`;

const Wrapper = styled.div`
  padding: 10px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Left = styled.div`
  flex: 1;
`;

const Logo = styled.div`
  font-weight: bold;
`;

const Right = styled.div`
  display: flex;
  align-items: center;

  ${mobile({
    flexDirection: "column",
    alignItems: "flex-start",
    position: "fixed",
    top: "0",
    right: "0",
    width: "230px",
    height: "100vh",
    backgroundColor: "#afe6ff",
    paddingTop: "90px",
    paddingLeft: "15px",
    transform: (props) =>
      props.isOpened ? "translateX(0)" : "translateX(100%)",
    transition: "0.35s ease-in-out",
    zIndex: 3,
  })}
`;

const NavMenuItem = styled.div`
  font-size: 14px;
  cursor: pointer;
  margin-left: 25px;
  ${mobile({ marginLeft: 5, marginBottom: 10 })}
`;

const NavItem = styled.span`
  position: relative;
  padding-bottom: 4px;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    width: ${({ active }) => (active ? '100%' : '0')};
    height: 2px;
    background-color: #1a438e;
    transition: width 0.3s ease;
  }

  &:hover::after {
    width: 100%;
  }
`;

const Hamburger = styled.div`
  display: none;
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 18px;
  cursor: pointer;
  z-index: 1001;

  ${mobile({ display: "block" })}
  ${tabletPortrait({ display: "block" })}
  ${tabletLandscape({ display: "block" })}

  span {
    position: absolute;
    height: 3px;
    width: 100%;
    background: #000;
    border-radius: 2px;
    transition: 0.3s ease;
  }

  span:nth-child(1) {
    top: ${({ open }) => (open ? "8px" : "0")};
    transform: ${({ open }) => (open ? "rotate(45deg)" : "none")};
  }

  span:nth-child(2) {
    opacity: ${({ open }) => (open ? 0 : 1)};
    top: 8px;
  }

  span:nth-child(3) {
    top: ${({ open }) => (open ? "8px" : "16px")};
    transform: ${({ open }) => (open ? "rotate(-45deg)" : "none")};
  }
`;

const MobileMenu = styled.div`
  display: none;
  flex: 1;
  justify-content: flex-end;

  ${mobile({ display: "flex" })}
  ${tabletPortrait({ display: "flex" })}
  ${tabletLandscape({ display: "flex" })}
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 2;   // lower than menu
  opacity: ${({ show }) => (show ? 1 : 0)};
  pointer-events: ${({ show }) => (show ? 'auto' : 'none')};
  transition: opacity 0.3s ease;
`;

/* ------------------ Navbar Component ------------------ */

const Navbar = () => {
  const { quantity } = useSelector((state) => state.cart);
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [menuOpen, setMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [hideNav, setHideNav] = useState(false);
  let lastScrollY = useRef(0);
  const ref = useRef(null);

  const activeTab = location.pathname.split("/")[1];

  /* ------------------ Outside Click Close ------------------ */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > lastScrollY.current && window.scrollY > 80) {
        setHideNav(true);
      } else {
        setHideNav(false);
      }
      lastScrollY.current = window.scrollY;
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "auto";
    return () => (document.body.style.overflow = "auto");
  }, [menuOpen]);

  const logout = () => {
    dispatch(emptyReducer());
    localStorage.removeItem("key");
    navigate("/");
  };

  return (
    <Container hide={hideNav}>
      <Wrapper>
        <Left>
          <Logo>
            <Link to="/">
              <img
                src={require("../Assets/Logo/Impact.png")}
                height={45}
                alt="Impact Technologies home"
              />
            </Link>
          </Logo>
        </Left>

        <Backdrop show={menuOpen} onClick={() => setMenuOpen(false)} />

        <Right isOpened={menuOpen}>
          {[
            { to: "/", label: "HOME" },
            { to: "/about", label: "ABOUT US" },
            { to: "/training", label: "TRAINING & PROJECTS" },
            { to: "/labs", label: "ROBOTICS & AI LABS" },
            { to: "/automation", label: "AUTOMATION/SOLUTION" },
            { to: "/products", label: "PRODUCTS" },
            { to: "/projects", label: "PROJECTS" },
          ].map(({ to, label }) => (
            <NavMenuItem key={to}>
              <NavLink
                to={to}
                style={{ color: "black", textDecoration: "none" }}
              >
                <NavItem active={activeTab === to.split("/")[1]}>
                  {label}
                </NavItem>
              </NavLink>
            </NavMenuItem>
          ))}

          {!user.isLoggedIn && (
            <>
              <NavMenuItem>
                <NavLink
                  style={{ color: "black", textDecoration: "none" }}
                  to="/register"
                >
                  REGISTER
                </NavLink>
              </NavMenuItem>
              <NavMenuItem>
                <NavLink
                  style={{ color: "black", textDecoration: "none" }}
                  to="/login"
                >
                  SIGN IN
                </NavLink>
              </NavMenuItem>
            </>
          )}

          {user.isLoggedIn && (
            <NavMenuItem>
              <Link to="/cart">
                <Badge badgeContent={quantity} color="primary">
                  <ShoppingCartOutlined
                    style={{
                      marginLeft: "0px",
                      textDecoration: "none",
                      color: "black",
                    }}
                  />
                </Badge>
              </Link>

              <Button onClick={(e) => setAnchorEl(e.currentTarget)}>
                <AccountCircle
                  fontSize="large"
                  style={{
                    marginLeft: "15px",
                    // color: '#378fa1',
                    fontSize: "28px",
                    textDecoration: "none",
                    color: "black",
                  }}
                />
              </Button>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                TransitionComponent={Fade}
              >
                <MenuItem onClick={() => navigate("/myOrders")}>
                  My Orders
                </MenuItem>
                <MenuItem onClick={logout}>Logout</MenuItem>
              </Menu>
            </NavMenuItem>
          )}
        </Right>

        <MobileMenu>
          <Hamburger open={menuOpen} onClick={() => setMenuOpen(!menuOpen)}>
            <span />
            <span />
            <span />
          </Hamburger>
        </MobileMenu>
      </Wrapper>
    </Container>
  );
};

export default Navbar;
